import { Injectable, Injector } from '@angular/core';
import {API_TYPE, HttpHelperService} from '../../../services/http-helper.service';
import { PHONE_CODE_DEFAULT_COUNTRYID } from './shared.service';
import { Modal, overlayConfigFactory } from 'src/app/modules/shared/modal/ngx-modal-compat';
import { ConfirmationPopupComponent } from '../components/confirmation-popup/confirmation-popup.component';
import { BSModalContext } from 'src/app/modules/shared/modal/ngx-modal-compat';
import { Router } from '@angular/router';
import {DatePipe} from "@angular/common";
import { environment } from '../../../../environments/environment';
import { MsalService } from '@azure/msal-angular';

export const SESSION_KEY_AUTH_USER = 'APP_AUTH_USER';
export const SESSION_KEY_AUTH_ROLES = 'APP_AUTH_ROLES';
export const SESSION_KEY_AUTH_ACCOUNT = 'APP_AUTH_ACCOUNT';
export const SESSION_KEY_AUTH_MEMBERSHIPS = 'APP_AUTH_MEMBERSHIPS';
export const SESSION_KEY_AUTH_POA_MEMBERSHIPS = 'APP_AUTH_POA_MEMBERSHIPS';
export const SESSION_KEY_AUTH_OWNERSHIPS = 'APP_AUTH_OWNERSHIPS';
export const SESSION_KEY_AUTH_POA_OWNERSHIPS = 'APP_AUTH_POA_OWNERSHIPS';
export const SESSION_KEY_AUTH_POA_DATA = 'APP_AUTH_POA_DATA';
/** The account the APP_AUTH_* entries were written for. */
export const SESSION_KEY_AUTH_OWNER = 'APP_AUTH_OWNER';
/** Prefix of the per-membership cache in MemberService: MEMB_<membershipId>. */
export const MEMBERSHIP_SESSION_KEY_PREFIX = 'MEMB_';
/** The return address the Bonus tab leaves in local storage for the manage-card pages. */
export const LOCAL_KEY_MANAGE_CARD = 'managecard';
/** The company the last opened membership belongs to; the interceptor sends it as the companyId header. */
export const SESSION_KEY_API_HEADER_COMPANY_ID = 'API_HEADER_COMPANY_ID';
/** A person an admin searched for: ownerships and fullmakt data (OwnershipService), memberships (MemberService). */
export const SESSION_KEY_ADMIN_SRCH_PERSON_OWNERSHIPS = 'ADMIN_SEARCH_PERSON_OWNERSHIPS';
export const SESSION_KEY_ADMIN_SRCH_PERSON_POA_DATA = 'ADMIN_SRCH_PERSON_POA_DATA';
export const SESSION_KEY_ADMIN_SRCH_PERSON_POA_OWNERSHIP_DATA = 'ADMIN_SRCH_PERSON_POA_OWNERSHIP_DATA';
export const SESSION_KEY_ADMIN_SRCH_PERSON_POA_MEMBERSHIP_DATA = 'ADMIN_SRCH_PERSON_POA_MEMBERSHIP_DATA';
export const SESS_KEY_ADMIN_SEARCH_PERSON_MEMBERSHIPS = 'SESS_KEY_ARMIN_SEARCH_PERSON_MEMBERSHIPS';
export const SESS_KEY_ADMIN_SEARCH_PERSON_POA_MEMBERSHIPS = 'SESS_KEY_ARMIN_SEARCH_PERSON_POA_MEMBERSHIPS';

export const SESSION_KEY_LOADING_PROFILE = 'LOADING_PROFILE';
export const SESSION_KEY_PHONE_VERIFICATION_DATA = 'PHONE_VERIFICATION';
export const SESSION_KEY_NABONET_ACCOUNT_OTP_VERIFIED = 'nabonet-account-otp-verified';
export const CREDENTIAL_TYPE_ID_EMAIL = 1;
export const CREDENTIAL_TYPE_ID_PHONE = 2;
export const CREDENTIAL_TYPE_ID_NABONET_ACCOUNT_VERIFICATION = 4;

export const ADMIN_ROLES = {
  SYSTEM_ADMIN: 'System Admin',
  BUSINESS_MANAGER_ADMIN: 'Business Manager Admin',
  BUSINESS_MANAGER_ADMIN_BETA: 'Business Manager Admin BETA',
  NABONETT_ADMIN_ROLE: 'Nabonett Admin'
};

/**
 * Removes everything the signed-in account left in the browser: the APP_AUTH_*
 * entries (user, roles, MSAL account, memberships, ownerships, fullmakt data,
 * the owner marker), the MEMB_<id> membership cache, the OTP flag, the two
 * transient flags, the company header, an admin's searched person and the
 * manage-card return address. MSAL's own entries stay, so the end-session call
 * on the interceptor's path still carries the account.
 *
 * AuthService.logoutUser() calls this; the Logg ut paths clear both storages
 * outright; CommonService calls it when the session turns out to belong to
 * another account.
 */
export function clearAccountSessionState(): void {
  const prefixes = ['APP_AUTH_', MEMBERSHIP_SESSION_KEY_PREFIX, SESSION_KEY_NABONET_ACCOUNT_OTP_VERIFIED + ':'];
  const keys: string[] = [
    SESSION_KEY_LOADING_PROFILE,
    SESSION_KEY_PHONE_VERIFICATION_DATA,
    SESSION_KEY_API_HEADER_COMPANY_ID,
    SESSION_KEY_ADMIN_SRCH_PERSON_OWNERSHIPS,
    SESSION_KEY_ADMIN_SRCH_PERSON_POA_DATA,
    SESSION_KEY_ADMIN_SRCH_PERSON_POA_OWNERSHIP_DATA,
    SESSION_KEY_ADMIN_SRCH_PERSON_POA_MEMBERSHIP_DATA,
    SESS_KEY_ADMIN_SEARCH_PERSON_MEMBERSHIPS,
    SESS_KEY_ADMIN_SEARCH_PERSON_POA_MEMBERSHIPS
  ];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && prefixes.some(prefix => key.startsWith(prefix))) {
      keys.push(key);
    }
  }
  keys.forEach(key => sessionStorage.removeItem(key));
  localStorage.removeItem(LOCAL_KEY_MANAGE_CARD);
}

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  constructor(
    private httpHelper: HttpHelperService,
    public modal: Modal,
    private router: Router,
    private datePipe: DatePipe,
    private injector: Injector
  ) {}

  /**
   * The session entries are served only to the account they were written for.
   * The live account comes from MSAL, looked up late so the specs that build
   * this service without MSAL keep working. localAccountId is the identity:
   * it is the same across the B2C policies, where homeAccountId is not.
   *
   * Reading the account walks MSAL's cache (several storage reads, parses and
   * a token decode), and a template binds these readers several times per
   * change-detection pass, so the answer is kept for the current synchronous
   * run and dropped at the next microtask. A login or a cross-tab change
   * happens in a later run and is seen there.
   */
  private currentAccountId(): string | null {
    if (this.liveAccountId === undefined) {
      this.liveAccountId = this.readLiveAccountId();
      queueMicrotask(() => { this.liveAccountId = undefined; });
    }
    return this.liveAccountId;
  }

  private liveAccountId: string | null | undefined;

  private readLiveAccountId(): string | null {
    const msal = this.injector.get(MsalService, null);
    const instance = msal ? msal.instance : null;
    if (!instance) {
      return null;
    }
    // Both logout paths empty MSAL's cache, so after a fresh login there is no
    // active account until the first request sets one; the first cached
    // account is what the token request and afterLoginProcess use as well.
    const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
    return account ? (account.localAccountId || account.homeAccountId || null) : null;
  }

  /**
   * Stamps the session with the account about to be written. Entries of
   * another account, or of no account, go first, so a logout that missed its
   * clear cannot leave one member's data under the next member's login.
   */
  private claimSession(accountId: string | null): void {
    if (!accountId) {
      return;
    }
    if (sessionStorage.getItem(SESSION_KEY_AUTH_OWNER) !== accountId) {
      clearAccountSessionState();
      sessionStorage.setItem(SESSION_KEY_AUTH_OWNER, accountId);
    }
  }

  /**
   * Read like the working app: return what is in session storage. Wiping on
   * a "wrong owner" read was clearing the user just written after B2C and
   * leaving the landing spinner up. A different account is cleared on write
   * via claimSession, which is when we know who signed in.
   */
  getOwnedSessionItem(key: string): string | null {
    return sessionStorage.getItem(key);
  }

  /** A write stamped for the live account, for entries other services keep. */
  setOwnedSessionItem(key: string, value: string): void {
    this.claimSession(this.currentAccountId());
    sessionStorage.setItem(key, value);
  }

  /**
   * Checks if the current environment is production.
   * Uses the `production` property from Angular environment files.
   *
   * @returns {boolean} True if production, false otherwise
   */
  isProductionEnv(): boolean {
    return environment.production === true;
  }

  // -- function to display confirmation dialog
  showConfirmation(
    paramObj: any,
    proceedCallBackFunc: any,
    cancelCallBackFunc?: any
  ) {
    const dialog = this.modal.open(
      ConfirmationPopupComponent,
      overlayConfigFactory(paramObj, BSModalContext)
    );
    dialog.result.then((data) => {
      if (data) {
        proceedCallBackFunc.call();
      } else if (cancelCallBackFunc) {
        cancelCallBackFunc.call();
      }
    });
  }

  /** An empty answer clears the stored list; a missing one leaves it alone. */
  setAuthMemberships(data: any) {
    this.claimSession(this.currentAccountId());
    if (Array.isArray(data) && data.length) {
      sessionStorage.setItem(
        SESSION_KEY_AUTH_MEMBERSHIPS,
        JSON.stringify(data)
      );
    } else if (Array.isArray(data)) {
      sessionStorage.removeItem(SESSION_KEY_AUTH_MEMBERSHIPS);
    }
  }

  setAuthPoaMemberships(data: any) {
    this.claimSession(this.currentAccountId());
    if (Array.isArray(data) && data.length) {
      sessionStorage.setItem(
        SESSION_KEY_AUTH_POA_MEMBERSHIPS,
        JSON.stringify(data)
      );
    } else if (Array.isArray(data)) {
      sessionStorage.removeItem(SESSION_KEY_AUTH_POA_MEMBERSHIPS);
    }
  }

  getAuthMemberships() {
    let parsedAuthData: any = [];
    let parsedAuthPoaData: any = [];
    const authData = this.getOwnedSessionItem(SESSION_KEY_AUTH_MEMBERSHIPS);
    const authPoaData = this.getOwnedSessionItem(SESSION_KEY_AUTH_POA_MEMBERSHIPS);
    if (authData) {
      parsedAuthData = JSON.parse(authData);
    }
    if (authPoaData) {
      parsedAuthPoaData = JSON.parse(authPoaData);
      parsedAuthPoaData.forEach((item: any) => {
        item.isPoaItem = true;
        parsedAuthData.push(item);
      })
    }
    const activeMemberss: any = [];
    parsedAuthData.forEach((item: any) => {
      if(item.membershipTypeInfo){
        if(item.membershipTypeInfo.code != 'U'){
          activeMemberss.push(item);
        }
      }else{ // ALLOWING OTHER MEMBERSHIP RECORDS WITHOUT TYPE INFO JUST IN CASE POA API DOESNT HAVE
        activeMemberss.push(item);
      }
    })
    return activeMemberss.length ? activeMemberss : null ;
  }



  // -------------
  setAuthOwnerships(data: any) {
    this.claimSession(this.currentAccountId());
    if (Array.isArray(data) && data.length) {
      data.forEach(item => {
        const addressData = item.propertyUnitDetails && item.propertyUnitDetails.address;
        item['title'] = this.getFormattedAddress(addressData);
        item['ref_person_number'] =  this.getAuthNIN();
        item['ownership_start_date'] = item['startDate']? this.datePipe.transform(item['startDate'], 'yyyy-MM-dd') : '';

      })

      sessionStorage.setItem(SESSION_KEY_AUTH_OWNERSHIPS, JSON.stringify(data));
    } else if (Array.isArray(data)) {
      sessionStorage.removeItem(SESSION_KEY_AUTH_OWNERSHIPS);
    }
  }

/*  setAuthPoaOwnerships(data: any) {
    if (Array.isArray(data) && data.length) {
      data.forEach(item => {
        const addressData = item.propertyUnitDetails.address;
        item['title'] = this.getFormattedAddress(addressData);
        item['ref_person_number'] =  this.getAuthNIN();
        item['ownership_start_date'] = item['startDate']? this.datePipe.transform(item['startDate'], 'yyyy-MM-dd') : '';
      })

      sessionStorage.setItem(SESSION_KEY_AUTH_POA_OWNERSHIPS, JSON.stringify(data));
    }
  }*/

  setAuthPoaData(data: any){
    this.claimSession(this.currentAccountId());
    sessionStorage.setItem(SESSION_KEY_AUTH_POA_DATA, JSON.stringify(data));
    // -- GETTING
    if(data.globalPowerOfAttorneyRoles){
      let allOwnerships = [];
      const poaOwnerships: any = this.getPoaOwnershipsFromData(data.globalPowerOfAttorneyRoles);
      const poaOwnershipsLcl: any = this.getPoaOwnershipsFromData(data.localPowerOfAttorneyRoles);
      if(poaOwnerships.length){
        allOwnerships = poaOwnerships;
      }
      if(poaOwnershipsLcl.length){
        allOwnerships = allOwnerships.concat(poaOwnershipsLcl);

      }
      if(allOwnerships.length){
        sessionStorage.setItem(SESSION_KEY_AUTH_POA_OWNERSHIPS, JSON.stringify(allOwnerships));
      }

    }
  }

  getAuthPoaData(){
    let data: any;
    const jsonStr = this.getOwnedSessionItem(SESSION_KEY_AUTH_POA_DATA);
    if(jsonStr){
      data = JSON.parse(jsonStr);
    }
    return data;
  }

  getPoaOwnershipsFromData(data: any){
    const ownershipsArr: any = [];
    if(Array.isArray(data) && data.length){
      data.forEach((item: any) => {
        const poaInfo: any = {
          assignedDateTime: item.assignedDateTime,
          customerInfo: item.customerInfo
        }
        if(Array.isArray(item.ownerships) && item.ownerships.length){
          item.ownerships.forEach((ownItem: any) => {
            const addressData = ownItem.propertyUnitDetails && ownItem.propertyUnitDetails.address;
            ownItem['title'] = this.getFormattedAddress(addressData);
            ownItem['ref_person_number'] =  this.getAuthNIN();
            ownItem['ownership_start_date'] = ownItem['startDate']? this.datePipe.transform(ownItem['startDate'], 'yyyy-MM-dd') : '';
            ownItem.poaInfo = poaInfo;
            ownershipsArr.push(ownItem);
          })
        }
      })
    }
    return ownershipsArr;
  }

  getAuthOwnerships() {
    let parsedAuthData: any = [];
    let parsedAuthPoaData: any = [];
    const authData = this.getOwnedSessionItem(SESSION_KEY_AUTH_OWNERSHIPS);
    const authPoaData = this.getOwnedSessionItem(SESSION_KEY_AUTH_POA_OWNERSHIPS);
    if (authData) {
      parsedAuthData = JSON.parse(authData);
    }
    if (authPoaData) {
      parsedAuthPoaData = JSON.parse(authPoaData);
      parsedAuthPoaData.forEach((item: any) => {
        item.isPoaItem = true;
        parsedAuthData.push(item);
      })
    }
    return parsedAuthData.length ? parsedAuthData : null ;
  }

  getAuthPoaOwnerships() {
    let parsedAuthData: any;
    const authData = this.getOwnedSessionItem(SESSION_KEY_AUTH_POA_OWNERSHIPS);
    if (authData) {
      parsedAuthData = JSON.parse(authData);
    }
    return parsedAuthData;
  }

  getAuthOwnership(ownershipId: any) {
    const sessOwnerships: any = this.getAuthOwnerships();
    let matchOwnership: any;
    if(Array.isArray(sessOwnerships)){
      sessOwnerships.forEach((item: any) => {
        if(item.ownershipId && item.ownershipId == ownershipId){
          matchOwnership = item;
        }
      })
    }
    return matchOwnership;
  }

  resetPoaData(){
    sessionStorage.removeItem(SESSION_KEY_AUTH_POA_OWNERSHIPS);
  }
  setAuthUser(data: any) {
    if (data) {
      this.claimSession(this.currentAccountId());
      sessionStorage.setItem(SESSION_KEY_AUTH_USER, JSON.stringify(data));
    }
  }

  setAuthRoles(data: any) {
    if (data) {
      this.claimSession(this.currentAccountId());
      sessionStorage.setItem(SESSION_KEY_AUTH_ROLES, JSON.stringify(data));
    }
  }

  getAuthRoles() {
    let parsedAuthData: any;
    const sessData = this.getOwnedSessionItem(SESSION_KEY_AUTH_ROLES);
    if (sessData) {
      parsedAuthData = JSON.parse(sessData);
    }
    return parsedAuthData;
  }

  isAuthUserHasAdminRole() {
    const authRoles = this.getAuthRoles();
    let hasAdminRole = false;
    const allowedRoles = [
      ADMIN_ROLES.SYSTEM_ADMIN,
      ADMIN_ROLES.BUSINESS_MANAGER_ADMIN,
      ADMIN_ROLES.BUSINESS_MANAGER_ADMIN_BETA,
      ADMIN_ROLES.NABONETT_ADMIN_ROLE
    ];
    if (Array.isArray(authRoles)) {
      authRoles.forEach((item) => {
        if (allowedRoles.includes(item.roleName)) {
          hasAdminRole = true;
        }
      });
    }
    // hasAdminRole = false;
    return hasAdminRole;
  }
  checkIfUserHasAnyValidVendorRole() {
    const authRoles = this.getAuthRoles();
    let hasRole = false;
    if (Array.isArray(authRoles)) {
      authRoles.forEach((item) => {
        const roleLowered = item.roleName.toLowerCase();
        if (!roleLowered.includes('delete')) {
          hasRole = true;
        }
      });
    }
    return hasRole;
  }

  /** Called on every login; a different account than last time starts from an empty session. */
  setAuthAccount(data: any) {
    if (data) {
      this.claimSession(data.localAccountId || data.homeAccountId || null);
      sessionStorage.setItem(SESSION_KEY_AUTH_ACCOUNT, JSON.stringify(data));
    }
  }

  getAuthAccount() {
    let parsedAuthData: any;
    const authData = this.getOwnedSessionItem(SESSION_KEY_AUTH_ACCOUNT);
    if (authData) {
      parsedAuthData = JSON.parse(authData);
    }
    return parsedAuthData;
  }

  getAuthAzureId() {
    const authAc: any = this.getAuthAccount();
    if (authAc && authAc.localAccountId) {
      return authAc.localAccountId;
    }
    return null;
  }

  getAuthSessionId() {
    const authAc: any = this.getAuthAccount();
    if (authAc && authAc.homeAccountId) {
      return authAc.homeAccountId;
    }
    return null;
  }

  getAuthUser() {
    let parsedAuthData: any;
    const authData = this.getOwnedSessionItem(SESSION_KEY_AUTH_USER);
    if (authData) {
      parsedAuthData = JSON.parse(authData);
    }
    return parsedAuthData;
  }

  getAuthAttr(attr: any) {
    const userDetails = this.getAuthUser();
    if (userDetails && userDetails[attr]) {
      return userDetails[attr];
    }
    return null;
  }

  getAuthUserId() {
    return this.getAuthAttr('personId');
  }

  isConsentSubmitted() {
    return this.getAuthAttr('consentSubmitted') ? true : false;
  }
  isConsentGiven() {
    return this.getAuthAttr('consentValue') ? true : false;
  }

  getAuthNIN() {
    const verifiedData: any = this.getAuthAttr('nationalIdentity');
    if (verifiedData && verifiedData['nin']) {
      return verifiedData['nin'];
    }
    return '';
  }

  isPersonVerified() {
    return this.getAuthNIN() ? true : false;
  }

  getAuthEmail() {
    return this.getAuthAttr('email');
  }
  getAuthName() {
    return this.getAuthAttr('name');
  }
  getAuthPhone() {
    return this.getAuthAttr('phone');
  }

  getAuthPhoneCountryId() {
    return this.getAuthAttr('countryId')
      ? this.getAuthAttr('countryId')
      : PHONE_CODE_DEFAULT_COUNTRYID;
  }

  isAuthEmailContainsSpecialDomain(): boolean {
    return this.isAuthUserHasAdminRole();
    /* let domainMatched = false;
    let email: any = this.getAuthEmail();
    if(email){
      email = email.toLowerCase();
      SPECIAL_DOMAINS.forEach((domain: any) => {
        if(email.includes(domain)){
          domainMatched = true;
        }
      });
      if(SPECIAL_ADMIN_EMAILS.includes(email)) {
        domainMatched = true;
      }
    }

    return domainMatched;*/
  }

  /**
   * Checks if the authenticated user's email is allowed to access beta functionality.
   * Returns true if the email matches any allowed domain (e.g. '@styret.com') or is in the allowed emails list.
   * When no lists are provided, uses SPECIAL_DOMAINS and SPECIAL_ADMIN_EMAILS.
   * @param allowedDomains Optional list of domains to allow (e.g. ['@styret.com', '@wingsystech.com'])
   * @param allowedEmails Optional list of exact emails to allow (e.g. ['tester@example.com'])
   * @returns true if the current user's email is allowed for beta
   */
  isAuthEmailAllowedForBeta(allowedDomains?: string[], allowedEmails?: string[]): boolean {
    const email = this.getAuthEmail();
    if (!email || typeof email !== 'string') {
      return false;
    }
    const normalizedEmail = email.toLowerCase().trim();
    const domains = allowedDomains?.length ? allowedDomains : SPECIAL_DOMAINS;
    const emails = allowedEmails?.length ? allowedEmails : SPECIAL_ADMIN_EMAILS;

    const domainMatch = domains.some(domain => normalizedEmail.includes(domain.toLowerCase()));
    const directMatch = emails.some(allowed => allowed.toLowerCase().trim() === normalizedEmail);
    return domainMatch || directMatch;
  }

  isAuthEmailVerified() {
    return true; // this.getAuthAttr('isEmailVerified');
  }

  isAuthPhoneVerified() {
    return this.getAuthAttr('isPhoneVerified');
  }

  getUserInfo() {
    return this.httpHelper.get('LoggedInUserDetails/getUserInfo', {});
  }
  generateVerificationOTP(formData: any) {
    return this.httpHelper.post('Persons/generateVerificationOTP', formData);
  }

  verifyOTP(formData: any) {
    return this.httpHelper.post('Persons/verifyOTP', formData);
  }

  private getNabonettAccountOtpSessionKey(): string {
    const personId = this.getAuthUserId();
    return `${SESSION_KEY_NABONET_ACCOUNT_OTP_VERIFIED}:${personId || ''}`;
  }

  isNabonettAccountVerifiedThisSession(): boolean {
    return sessionStorage.getItem(this.getNabonettAccountOtpSessionKey()) === 'true';
  }

  setNabonettAccountVerifiedThisSession(): void {
    sessionStorage.setItem(this.getNabonettAccountOtpSessionKey(), 'true');
  }

  updateAccountInfo(formData: any) {
    return this.httpHelper.post('Persons/updateAccountInfo', formData);
  }

  updateAccountConsent(formData: any) {
    return this.httpHelper.post('Persons/updateAccountConsent', formData);
  }

  getOwnershipDetailsForNabonett(params: any) {
    return this.httpHelper.get(
      'companyprocesses/getOwnershipDetailsForNabonett',
      params
    );
  }

  getOverviewDetailsForNabonett(params: any) {
    return this.httpHelper.get(
      'companyprocesses/getOverviewDetailsForNabonett',
      params
    );
  }

  getMembershipCustomerInvoicesForNabonett(params: any) {
    return this.httpHelper.get(
      'companyprocesses/getMembershipCustomerInvoicesForNabonett',
      params
    );
  }

  downloadInvoice(CompanyId: any, InvoiceId: any) {
    return this.httpHelper.get('CommonInvoicing/getInvoicePdfWrapper', {
      CompanyId,
      InvoiceId,
    });
  }
  getFormattedAddress(srcObj: any, objType?: string): unknown {
    let addressStr = '';
    if(!srcObj){
      return addressStr;
    }
    // -------------------
    const displayCountry: any = false;
    // -------------------
    const address1Keys = ['address1', 'addressLine1', 'Address1', 'AddressLine1'];
    const address2Keys = ['address2', 'addressLine2', 'Address2', 'AddressLine2'];
    const addressNoKeys = ['addressNumber', 'AddressNumber', 'AdressNumber'];
    const zipCodeKeys = ['zipcode', 'ZipCode', 'zip', 'Zipcode', 'zipCode'];
    const cityKeys = ['city', 'City'];
    const addressLetterKeys = ['addressLetter', 'AddressLetter'];
    const countryKeys = ['country', 'Country'];
    const obj: any = {};

    obj.addressLine1 = this.getMatchingValueForKeys(srcObj, address1Keys);
    obj.addressLine2 = this.getMatchingValueForKeys(srcObj, address2Keys);
    obj.addressNumber = this.getMatchingValueForKeys(srcObj, addressNoKeys);
    obj.zipcode = this.getMatchingValueForKeys(srcObj, zipCodeKeys);
    obj.city = this.getMatchingValueForKeys(srcObj, cityKeys);
    obj.country = this.getMatchingValueForKeys(srcObj, countryKeys);
    obj.addressLetter = this.getMatchingValueForKeys(srcObj, addressLetterKeys);
    // ---------------
    const addressDataArray: any = [];

    if (obj.addressLine1){
      addressStr = obj.addressLine1 + '  ';
    }
    if (obj.addressLine2){
      addressStr += obj.addressLine2 + '  ';
    }
    if (obj.addressNumber){
      addressStr += obj.addressNumber + '  ';
    }
    if (obj.addressLetter){
      addressStr += obj.addressLetter + '  ';
    }

    let hasZipCode = false;
    if (obj.zipcode){
      const zipStr = obj.zipcode;
      addressStr = addressStr.trim();
      addressStr = addressStr + ', ';
      addressStr += zipStr;
      hasZipCode = true;
    }
    if (obj.city){
      addressStr = addressStr.trim();
      const zipCityJoiner = hasZipCode ? ' ' : ', ';
      addressStr = addressStr + zipCityJoiner;
      addressStr += obj.city + '  ';
    }

    if (displayCountry && obj.country){
      const joiner = addressStr ? ' ' : '';
      addressStr += (joiner + obj.country);
    }

    if (addressStr.length > 2){
      addressStr = addressStr.trim();
      addressStr = addressStr.replace(/(^,)|(,$)/g, '');
    }
    return addressStr;
  }

  getMatchingValueForKeys(sourceObj: any, keys: any){
    let val = '';
    if (Array.isArray(keys)){
      keys.forEach((key: any) => {
        if (sourceObj[key]){
          val = sourceObj[key];
        }
      });
    }
    return val;
  }


  getShortAddressTitle(obj: any, objType?: string): unknown {
    let addressStr = '';
    // ---------------
    const addressDataArray: any = [];
    if (obj.AddressLine1) {
      addressStr = obj.AddressLine1 + '  ';
    }
    if (obj.AddressLine2) {
      addressStr += obj.AddressLine2 + '  ';
    }
    if (obj.AdressNumber) {
      addressStr += obj.AdressNumber + '  ';
    }

    if (addressStr.length > 2) {
      addressStr = addressStr.trim();
      addressStr = addressStr.replace(/(^,)|(,$)/g, '');
    }
    return addressStr;
  }

  updateLastLogin(AzureID: any, Email: any) {
    const PlatformType = 'NABONETT';
    return this.httpHelper.post('UpdateLastLogin/updateLoginDateTime', {
      AzureID,
      Email,
      PlatformType,
    });
  }

  public getNabonettDocuments(
    personNumber: any,
    customerId: any,
    companyid: any
  ) {
    const additionalHeaders: any = [{ key: 'companyid', value: companyid }];
    const params: any = { personNumber, customerId };
    return this.httpHelper.getWithExtraHeaders(
      'CompanyDocument/getNabonettDocuments',
      additionalHeaders,
      params
    );
  }

  public downloadDocument(
    personNumber: any,
    customerId: any,
    companyid: any,
    documentId: any,
    source: any
  ) {
    const additionalHeaders: any = [{ key: 'companyid', value: companyid }];
    const params: any = { personNumber, customerId, documentId, source };
    return this.httpHelper.getBufferWithExtraHeaders(
      'CompanyDocument/downloadNabonettDocument',
      additionalHeaders,
      params
    );
  }

  AddContactPerson(formData: any) {
    return this.httpHelper.post('Nabonett/AddContactPerson', formData);
  }

  getTpoDetailByPersonNo(personNo: any) {
    return this.httpHelper.get('Nabonett/GetTpoDetailByPersonNo', { personNo });
  }
  downloadTpoFileForNabonett(params: any) {
    return this.httpHelper.get('Nabonett/DownloadTpoFileForNabonett', params);
  }

  getCountryList() {
        return this.httpHelper.get('Country/GetCountryList');
    }

  redirectToDefaultPage() {
    const hasOwnerships = this.getAuthOwnerships()? true : false;
    const hasMemberships = this.getAuthMemberships()? true : false;
    if (this.getAuthNIN() && hasOwnerships ) {
      this.router.navigate(['/account/ownerships']);
    } else if (this.getAuthNIN() && hasMemberships) {
      this.router.navigate(['/account/memberships']);
    } else {
      this.router.navigate(['/account/profile']);
    }
  }

  /**
   * GetMemberships is the slowest call of the login group, and the landing page
   * no longer waits for it. The flag lets the menu hold the membership item's
   * place while it runs instead of letting the menu jump when it answers. It is
   * kept in memory rather than in session storage on purpose: a reload starts
   * from "not loading", which is the safe reading.
   */
  private membershipsLoading = false;

  setMembershipsLoading(isLoading: boolean) {
    this.membershipsLoading = isLoading;
  }

  isMembershipsLoading(): boolean {
    return this.membershipsLoading;
  }

  setProfileLoading(isLoading: any) {
    sessionStorage.setItem(
      SESSION_KEY_LOADING_PROFILE,
      isLoading ? 'TRUE' : 'FALSE'
    );
  }

  isProfileLoading() {
    const val = sessionStorage.getItem(SESSION_KEY_LOADING_PROFILE);
    return val == 'TRUE' ? true : false;
  }

  storeLog(type: any, message: any, data?: any){
    const dataJsonStr = data? JSON.stringify(data): '';
    const authAzureId = this.getAuthAzureId();
    const authEmail = this.getAuthEmail();
    const authPersonId = this.getAuthUserId();
    const authSessionId = this.getAuthSessionId();
    const formData: any = {
      source: 'NABONETT',
      type: type,
      email: authEmail,
      message: message,
      sessionId: authSessionId,
      data: dataJsonStr
    };
    return this.httpHelper.post('uiinteraction/savelog', formData, API_TYPE.SIGNVERIFY_API);
  }

  storePhoneVerificationData(data: any){
    const dataObj = {createdAt: +new Date, data};
    sessionStorage.setItem(
      SESSION_KEY_PHONE_VERIFICATION_DATA,
      JSON.stringify(dataObj)
    );
  }

  clearPhoneVerificationData(){
    sessionStorage.removeItem(SESSION_KEY_PHONE_VERIFICATION_DATA);
  }

  getPhoneVerificationData(){
    const sessData = sessionStorage.getItem(SESSION_KEY_PHONE_VERIFICATION_DATA);
    var FIVE_MINS = 5 * 60 * 1000; /* ms */
    let responseData: any;
    if(sessData){
        const dataObj: any =  JSON.parse(sessData);
        if(dataObj.createdAt){
          var curDate: any = new Date();
          var storedDate: any = new Date(parseInt(dataObj.createdAt));
          const lapsedTime = (curDate - storedDate)
          if(lapsedTime < FIVE_MINS){
            responseData = {data: dataObj.data , lapsedSeconds: Math.trunc((FIVE_MINS - lapsedTime)/1000) };
          }
        }
    }
    return responseData;
  }


}

export const LOG_TYPES = {
  INFO: 'INFO',
  ERROR: 'ERROR',
  SUCCESS: 'SUCCESS',
  WARNING: 'WARNING',
}

export const NABONETT_MAIN_CLASS = 'nabonett-site';

export const NO_TXT = {
  COMMON: {
    OK: 'Ok',
    CANCEL: 'Kansellere',
    CONFIRM: 'Bekrefte',
    BACK: 'Tilbake',
    SEARCH: 'Søk',
    NO_RECORDS_FOUND: 'Ingen oppføring funnet',
    LOADING_DATA: 'Laster inn data',
    REQ_ERROR_CONTACT_ADMIN: 'Det har oppstått en feil under forsøk på å behandle forespørselen din. Vennligst kontakt admin.'
  },
  MEMBERSHIP: {
    NO_RECORDS_FOUND: 'Ingen ubetalte kontingenter',
    NOT_FOUND:
      'Vi finner ikke dette medlemskapet. Gå til Mine medlemskap for å velge blant medlemskapene dine.',
  },
  MEMBONUS: {
    BONUS_AVAILABLE: 'Tilgjengelig Bonus',
    AMT_TO_PAYOUT: 'Beløp til utbetaling',
    AC_FOR_PAYOUT: 'Kontonummer for utbetaling',
    FOR_EARNING: 'Kort lagt inn for opptjening',
    ORDER_PAYOUT: 'Bestill utbetaling',
    CHANGE_AC_NO: 'Endre kontonummer',
    DELETE_AC_NO: 'Slett kontonummer',
    IN_CASE_OF_PAYMENT_TXT:
      'Ved utbetaling vil det ta opp til flere arbeidsdager før pengene står på konto',
    YOU_HAVE_NOT_ADDED_TXT:
      'Du har ikke lagt inn noen kort eller konti for opptjening av bonus',
    DEBIT_CARD_TXT:
      'Har du bankkort med BankAxept-logo, må du registrere ditt kontonummer som du finner på baksiden av kortet. Du kan bare legge til kort som står i ditt navn!',
    CREDIT_CARD_TXT: 'Du kan bare legge til kort som står i ditt navn!',
    NEW_ACC_SAME_EXISTING_ACC:
      'nytt kontonummer er det samme som eksisterende kontonummer',
    ACC_UPDATED_SUCCESSFULLY: 'Kontonummeret har blitt oppdatert',
    INVALID_ACCOUNTNO: 'Ugyldig kontonummer',
    NOT_RESPONDING_ERROR:
      'Vi beklager! Medlemsprogrammet svarer ikke for øyeblikket og vi kan derfor ikke vise deg dine fordeler og bonus. Prøv igjen senere så bør dette være tilgjengelig igjen.',
    // The line above names both, and the benefits call can fail on its own while
    // the bonus figure beside it loads. (WO-35)
    BENEFITS_NOT_RESPONDING:
      'Vi beklager! Medlemsprogrammet svarer ikke for øyeblikket og vi kan derfor ikke vise deg dine fordeler. Prøv igjen senere så bør dette være tilgjengelig igjen.',
  },
  SECURITY_OTP: {
    TITLE: 'Sikkerhetsbekreftelse',
    HEADING: 'Bekreft med engangskode',
    SENDING_MSG: 'Sender engangskode…',
    CHOOSE_CHANNEL: 'Velg hvordan du vil motta engangskoden',
    CHANNEL_EMAIL: 'E-post',
    CHANNEL_SMS: 'Mobil',
    SEND_OTP_BTN: 'Send engangskode',
    CHANGE_CHANNEL: 'Velg annen metode',
    BODY_PREFIX: 'For å fortsette må du bekrefte med en engangskode sendt til',
    BODY_SUFFIX: 'DSkriv inn koden under for å fortsette.',
    CONFIRM_CODE_BTN: 'Bekreft kode',
    RESEND_OTP_BTN: 'Send engangskode på nytt',
  },
  ALERT_TITLE: {
    SUCCESS: 'Fullført!',
    FAILED: 'Feil',
    ERROR: 'Error',
  },
  OTP_VERIFIED_SUCCESSFULLY: 'Engangskoden er godtatt.',
  PROFILE: {
    UNVERIFIED_EMAIL_TO_VERIFY: 'Din epost er ikke bekreftet.',
    CLICK_HERE_TO_VERIFY: 'Klikk her for å bekrefte',
    UNVERIFIED_PHONENO_TO_VERIFY: 'Ditt mobilnummer er ikke bekreftet.',
    FAILED_SENDING_EMAIL_OTP:
      'Feil oppstod når vi forsøkte å sende deg kode på epost',
    EMAIL_VERIFICATION_OTP_SENT: 'Engangskode sendt på epost',
    FAILED_SENDING_CONTACTNO_OTP:
      'Feil oppstod når vi forsøkte å sende deg kode på telefon',
    CONTACTNO_VERIFICATION_OTP_SENT: 'Engangskode sendt på telefon',
    NUMBER_ALREADY_IN_USED: 'Nummer som allerede er brukt av en annen person.',
    NUMBER_UPDATED_SUCCESSFULLY: 'Nummeret er oppdatert.',

    INVALID_OTP_MSG: 'Ugyldig engangskode',
    DIDNT_GET_OTP_RETRY_SENDING_MSG:
      'Engangskode sendt nylig. Du kan sende på nytt om:',
    RESEND_OTP_BTN: 'Send engangskode på nytt',
    OTP_EXPIRED_MSG: 'Engangskoden har utløpt, vennligst forsøk igjen',
    ERROR_OCCURED_MSG:
      'Vi beklager - en feil har skjedd. Vennligst forsøk igjen. ',
    OTP_VERIFIED_SUCCESSFULLY: 'Engangskoden er godtatt.',

    USER_IDENTIFICATION_PENDING: 'Brukeridentifikasjon venter',
    USER_IDENTIFIED_SUCCESSFULLY: 'Bruker identifisert',
    EMAIL_IS_VERIFIED: 'E-post er bekreftet.',

    EMAIL_VERIFICATION_PENDING: 'E-postbekreftelse venter.',
    TELEFON_IS_VERIFIED: 'Telefonen er verifisert.',
    TELEFON_VERIFICATION_PENDING: 'Telefonbekreftelse venter.',

    CONSENT_GIVEN_BUTTON_RETRACT_TITLE: 'SAMTYKKE',
    CONSENT_GIVEN_BUTTON_RETRACT_P1: 'Din epost, mobiltelefon, bankkonto og adresse som du legger inn her, holdes oppdatert hos alle organisasjoner hvor du er registrert som eier, medlem eller bruker. Du kan se alle selskaper/organisasjoner dette gjelder, i menyen. ',
    CONSENT_GIVEN_BUTTON_RETRACT_P2: 'Dersom du trekker tilbake samtykke, vil informasjonen hos disse organisasjonene ikke lenger holdes aktiv.',

    CONSENT_NOT_GIVEN_BUTTON_GIVE_TITLE:'SAMTYKKE IKKE GITT',
    CONSENT_NOT_GIVEN_BUTTON_GIVE_P1: 'Din kontaktinformasjon og informasjon fra nabonett blir ikke sendt videre til selskaper/organisasjoner hvor du er eier, medlem eller bruker.',
    CONSENT_NOT_GIVEN_BUTTON_GIVE_P2: 'Dersom du gir samtykke vil epost, mobiltelefon, bankkonto og adresse som du legger inn her, holdes oppdatert hos alle organisasjoner hvor du er registrert som eier, medlem eller bruker.',

    BETA_SECTION_TOOLTIP: 'Denne seksjonen er for øyeblikket kun tilgjengelig for betabrukere.',
    CONFIRM_TITLE_AC_NUMBER: 'Bekreft kontonummer', // ENG'Confirm Account Number';
    CONFIRM_AC_SYNC_FROM_PIVOTAL: 'Ditt medlemskap har et registrert kontonummer i medlemssystemet. Vær vennlig bekreft at det er riktig for å legge kontonummeret i din profil. Det vil bli brukt til å utbetale dine utlegg, styrehonorar, medlemsbonus og/eller utbetalinger i forbindelse med din bolig.'
                        // ENG - Your membership has a registered account number in the membership system. Please confirm that it is correct so that the account number can be added to your profile. It will be used to pay out your expenses, board fees, member bonus and/or payments related to your home.
  },
  CONSENT_POPUP: {
    CONSENT_UPDATED_MSG: 'Samtykke er lagret',
    ERROR_OCCURED_UPDATE_CONSENT_MSG:
      'En feil oppstod da vi forsøkte å oppdatere ditt samtykke eller avslag',
  },
  OVERVIEW: {
    SYS_ADMIN_SEARCH_BIRTHNO_TXT:
      'Som systemadministrator kan du søke og sjekke enhver person etter fødselsnummer.',
  },
  BANKID: {
    BANKID_VERIFICATION: 'Bekreftelse med BankID',
    VERIFY_YOUR_AC_TXT: 'Bekreft din identitet med BankID',
    BTN_VERIFY_NOW: 'Bekreft nå',
    VERIFICATION_SUCCESS: 'ID bekreftet',
    VERIFICATION_SUCCESS_TEXT: 'Vi har bekreftet din identitet med BankID.',
    PROCEED_TO_PROFILE: 'Du kan nå gå videre til din profil',
    VERIFICATION_ABORTED: 'Bekreftelse avbrutt',
    VERIFICATION_ABORTED_TEXT: 'ID-bekreftelsen ble avbrutt.',
    VERIFICATION_FAILED: 'ID-bekreftelsen feilet',
    SOMETHING_WENT_WRONG: 'Noe gikk galt.',
    VERIFICATION_NOT_COMPLETED: 'ID-bekreftelsen kunne ikke fullføres.',
    PLEASE_TRY_AGAIN:
      'Vennligst forsøkt igjen etter å ha logget inn på din profil.',
  },
  TAX_REPORT: {
    TPO_MSG:
      'Opplysningene under er sendt til Skatteetaten, og kan kontrolleres under temaet «Boliger og eiendeler» i skattemeldingen.',
    TPO_WITH_CHANGE_MSG:
      'Opplysningene under er sendt til Skatteetaten, og må kontrolleres under temaet «Boliger og eiendeler» i skattemeldingen.',
    NO_TPO_MSG:
      'Ingen årsoppgaver tilgjengelig for deg.',
  },
  MEMBERSHIP_BONUS: {
    DELETE_CARD_SUCCESS_MSG: 'Kortet slettet vellykket',
    DELETE_CARD: 'Slett kort',
    AMOUNT_GREATER_0: 'Beløpet skal være større enn 0',
    CASHBACK_ORDERED: 'Cashback bestilt',
    CASHBACK_TITLE: 'Bestill utbetaling',
    CASHBACK_LESS_THAN_BONUS:
      'Ordrebetalingen skal være mindre enn eller lik tilgjengelig bonus',
    BANK_ACC_DELETED: 'Kontonummeret ble slettet',
  },
  EMAIL_CHANGE_USER_HAS_ROLES_MSG: 'Du har aktive roller på Styret.com. Etter at den nye e-postadressen din er bekreftet med BankID, vil den bli oppdatert for Nabonett. Din nåværende e-postadresse vil forbli aktiv for Styret.com.',
  EMAIL_CHANGE_USER_NO_ROLES_MSG: 'Når den nye e-postadressen din er bekreftet med BankID, vil den nåværende e-postadressen din bli deaktivert og erstattet med den nye e-postadressen.',
  //EMAIL_CHANGE_PROCESS_NOTE: 'Vi sender en bekreftelseslenke til den nye e-postadressen din. Etter at du har bekreftet den og fullført BankID-bekreftelsen, vil den tidligere e-postadressen din bli deaktivert.'
  EMAIL_CHANGE_PROCESS_NOTE: 'Du får tilsendt en lenke til den nye e-postadressen. Når du bekrefter med BankID, blir den nye e-postadressen ditt nye brukernavn.'
};

// export const SPECIAL_DOMAINS =  ['@styret.com', '@wingsystech.com', '@bori.no'];
export const SPECIAL_DOMAINS = [
   '@styret.com',
  // '@wingsystech.com',
  '@encourgant.com',
  // '@bori.no',
];
// export const SPECIAL_ADMIN_EMAILS =  ['jimin.chung@eggsdesign.com'];
export const SPECIAL_ADMIN_EMAILS = ['skanta.it@gmail.com', 'sk.lmpkpm@gmail.com'];


export const COUNTRIES_DATA: any = [
    {
        "Id": 1,
        "Name": "Afghanistan",
        "CountryCode": "AF",
        "DialingCode": "+93"
    },
    {
        "Id": 2,
        "Name": "Albania",
        "CountryCode": "AL",
        "DialingCode": "+355"
    },
    {
        "Id": 3,
        "Name": "Algeria",
        "CountryCode": "DZ",
        "DialingCode": "+213"
    },
    {
        "Id": 4,
        "Name": "American Samoa",
        "CountryCode": "AS",
        "DialingCode": "+1-684"
    },
    {
        "Id": 5,
        "Name": "Andorra, Principality of",
        "CountryCode": "AD",
        "DialingCode": "+376"
    },
    {
        "Id": 6,
        "Name": "Angola",
        "CountryCode": "AO",
        "DialingCode": "+244"
    },
    {
        "Id": 7,
        "Name": "Anguilla",
        "CountryCode": "AI",
        "DialingCode": "+1-264"
    },
    {
        "Id": 8,
        "Name": "Antarctica",
        "CountryCode": "AQ",
        "DialingCode": "+672"
    },
    {
        "Id": 9,
        "Name": "Antigua and Barbuda",
        "CountryCode": "AG",
        "DialingCode": "+1-268"
    },
    {
        "Id": 10,
        "Name": "Argentina",
        "CountryCode": "AR",
        "DialingCode": "+54"
    },
    {
        "Id": 11,
        "Name": "Armenia",
        "CountryCode": "AM",
        "DialingCode": "+374"
    },
    {
        "Id": 12,
        "Name": "Aruba",
        "CountryCode": "AW",
        "DialingCode": "+297"
    },
    {
        "Id": 13,
        "Name": "Australia",
        "CountryCode": "AU",
        "DialingCode": "+61"
    },
    {
        "Id": 14,
        "Name": "Austria",
        "CountryCode": "AT",
        "DialingCode": "+43"
    },
    {
        "Id": 15,
        "Name": "Azerbaijan or Azerbaidjan (Former Azerbaijan Soviet Socialist Republic)",
        "CountryCode": "AZ",
        "DialingCode": "+994"
    },
    {
        "Id": 16,
        "Name": "Bahamas, Commonwealth of The",
        "CountryCode": "BS",
        "DialingCode": "+1-242"
    },
    {
        "Id": 17,
        "Name": "Bahrain, Kingdom of (Former Dilmun)",
        "CountryCode": "BH",
        "DialingCode": "+973"
    },
    {
        "Id": 18,
        "Name": "Bangladesh (Former East Pakistan)",
        "CountryCode": "BD",
        "DialingCode": "+880"
    },
    {
        "Id": 19,
        "Name": "Barbados",
        "CountryCode": "BB",
        "DialingCode": "+1-246"
    },
    {
        "Id": 20,
        "Name": "Belarus (Former Belorussian [Byelorussian] Soviet Socialist Republic)",
        "CountryCode": "BY",
        "DialingCode": "+375"
    },
    {
        "Id": 21,
        "Name": "Belgium",
        "CountryCode": "BE",
        "DialingCode": "+32"
    },
    {
        "Id": 22,
        "Name": "Belize (Former British Honduras)",
        "CountryCode": "BZ",
        "DialingCode": "+501"
    },
    {
        "Id": 23,
        "Name": "Benin (Former Dahomey)",
        "CountryCode": "BJ",
        "DialingCode": "+229"
    },
    {
        "Id": 24,
        "Name": "Bermuda",
        "CountryCode": "BM",
        "DialingCode": "+1-441"
    },
    {
        "Id": 25,
        "Name": "Bhutan, Kingdom of",
        "CountryCode": "BT",
        "DialingCode": "+975"
    },
    {
        "Id": 26,
        "Name": "Bolivia",
        "CountryCode": "BO",
        "DialingCode": "+591"
    },
    {
        "Id": 27,
        "Name": "Bosnia and Herzegovina",
        "CountryCode": "BA",
        "DialingCode": "+387"
    },
    {
        "Id": 28,
        "Name": "Botswana (Former Bechuanaland)",
        "CountryCode": "BW",
        "DialingCode": "+267"
    },
    {
        "Id": 30,
        "Name": "Brazil",
        "CountryCode": "BR",
        "DialingCode": "+55"
    },
    {
        "Id": 32,
        "Name": "Brunei (Negara Brunei Darussalam)",
        "CountryCode": "BN",
        "DialingCode": "+673"
    },
    {
        "Id": 33,
        "Name": "Bulgaria",
        "CountryCode": "BG",
        "DialingCode": "+359"
    },
    {
        "Id": 34,
        "Name": "Burkina Faso (Former Upper Volta)",
        "CountryCode": "BF",
        "DialingCode": "+226"
    },
    {
        "Id": 35,
        "Name": "Burundi (Former Urundi)",
        "CountryCode": "BI",
        "DialingCode": "+257"
    },
    {
        "Id": 36,
        "Name": "Cambodia, Kingdom of (Former Khmer Republic, Kampuchea Republic)",
        "CountryCode": "KH",
        "DialingCode": "+855"
    },
    {
        "Id": 37,
        "Name": "Cameroon (Former French Cameroon)",
        "CountryCode": "CM",
        "DialingCode": "+237"
    },
    {
        "Id": 38,
        "Name": "Canada",
        "CountryCode": "CA",
        "DialingCode": "+1"
    },
    {
        "Id": 39,
        "Name": "Cape Verde",
        "CountryCode": "CV",
        "DialingCode": "+238"
    },
    {
        "Id": 40,
        "Name": "Cayman Islands",
        "CountryCode": "KY",
        "DialingCode": "+1-345"
    },
    {
        "Id": 41,
        "Name": "Central African Republic",
        "CountryCode": "CF",
        "DialingCode": "+236"
    },
    {
        "Id": 42,
        "Name": "Chad",
        "CountryCode": "TD",
        "DialingCode": "+235"
    },
    {
        "Id": 43,
        "Name": "Chile",
        "CountryCode": "CL",
        "DialingCode": "+56"
    },
    {
        "Id": 44,
        "Name": "China",
        "CountryCode": "CN",
        "DialingCode": "+86"
    },
    {
        "Id": 45,
        "Name": "Christmas Island",
        "CountryCode": "CX",
        "DialingCode": "+53"
    },
    {
        "Id": 46,
        "Name": "Cocos (Keeling) Islands",
        "CountryCode": "CC",
        "DialingCode": "+61"
    },
    {
        "Id": 47,
        "Name": "Colombia",
        "CountryCode": "CO",
        "DialingCode": "+57"
    },
    {
        "Id": 48,
        "Name": "Comoros, Union of the",
        "CountryCode": "KM",
        "DialingCode": "+269"
    },
    {
        "Id": 49,
        "Name": "Congo, Democratic Republic of the (Former Zaire)",
        "CountryCode": "CD",
        "DialingCode": "+243"
    },
    {
        "Id": 50,
        "Name": "Congo, Republic of the",
        "CountryCode": "CG",
        "DialingCode": "+242"
    },
    {
        "Id": 51,
        "Name": "Cook Islands (Former Harvey Islands)",
        "CountryCode": "CK",
        "DialingCode": "+682"
    },
    {
        "Id": 52,
        "Name": "Costa Rica",
        "CountryCode": "CR",
        "DialingCode": "+506"
    },
    {
        "Id": 53,
        "Name": "Cote D`Ivoire (Former Ivory Coast)",
        "CountryCode": "CI",
        "DialingCode": "+225"
    },
    {
        "Id": 54,
        "Name": "Croatia (Hrvatska)",
        "CountryCode": "HR",
        "DialingCode": "+385"
    },
    {
        "Id": 55,
        "Name": "Cuba",
        "CountryCode": "CU",
        "DialingCode": "+53"
    },
    {
        "Id": 56,
        "Name": "Cyprus",
        "CountryCode": "CY",
        "DialingCode": "+357"
    },
    {
        "Id": 57,
        "Name": "Czech Republic",
        "CountryCode": "CZ",
        "DialingCode": "+420"
    },
    {
        "Id": 59,
        "Name": "Denmark",
        "CountryCode": "DK",
        "DialingCode": "+45"
    },
    {
        "Id": 60,
        "Name": "Djibouti (Former French Territory of the Afars and Issas, French Somaliland)",
        "CountryCode": "DJ",
        "DialingCode": "+253"
    },
    {
        "Id": 61,
        "Name": "Dominica",
        "CountryCode": "DM",
        "DialingCode": "+1-767"
    },
    {
        "Id": 63,
        "Name": "East Timor (Former Portuguese Timor)",
        "CountryCode": "TP",
        "DialingCode": "+670"
    },
    {
        "Id": 64,
        "Name": "Ecuador",
        "CountryCode": "EC",
        "DialingCode": "+593 "
    },
    {
        "Id": 65,
        "Name": "Egypt (Former United Arab Republic - with Syria)",
        "CountryCode": "EG",
        "DialingCode": "+20"
    },
    {
        "Id": 66,
        "Name": "El Salvador",
        "CountryCode": "SV",
        "DialingCode": "+503"
    },
    {
        "Id": 67,
        "Name": "Equatorial Guinea (Former Spanish Guinea)",
        "CountryCode": "GQ",
        "DialingCode": "+240"
    },
    {
        "Id": 68,
        "Name": "Eritrea (Former Eritrea Autonomous Region in Ethiopia)",
        "CountryCode": "ER",
        "DialingCode": "+291"
    },
    {
        "Id": 69,
        "Name": "Estonia (Former Estonian Soviet Socialist Republic)",
        "CountryCode": "EE",
        "DialingCode": "+372"
    },
    {
        "Id": 70,
        "Name": "Ethiopia (Former Abyssinia, Italian East Africa)",
        "CountryCode": "ET",
        "DialingCode": "+251"
    },
    {
        "Id": 71,
        "Name": "Falkland Islands (Islas Malvinas)",
        "CountryCode": "FK",
        "DialingCode": "+500"
    },
    {
        "Id": 72,
        "Name": "Faroe Islands",
        "CountryCode": "FO",
        "DialingCode": "+298"
    },
    {
        "Id": 73,
        "Name": "Fiji",
        "CountryCode": "FJ",
        "DialingCode": "+679"
    },
    {
        "Id": 74,
        "Name": "Finland",
        "CountryCode": "FI",
        "DialingCode": "+358"
    },
    {
        "Id": 75,
        "Name": "France",
        "CountryCode": "FR",
        "DialingCode": "+33"
    },
    {
        "Id": 76,
        "Name": "French Guiana or French Guyana",
        "CountryCode": "GF",
        "DialingCode": "+594"
    },
    {
        "Id": 77,
        "Name": "French Polynesia (Former French Colony of Oceania)",
        "CountryCode": "PF",
        "DialingCode": "+689"
    },
    {
        "Id": 79,
        "Name": "Gabon (Gabonese Republic)",
        "CountryCode": "GA",
        "DialingCode": "+241"
    },
    {
        "Id": 80,
        "Name": "Gambia, The",
        "CountryCode": "GM",
        "DialingCode": "+220"
    },
    {
        "Id": 81,
        "Name": "Georgia (Former Georgian Soviet Socialist Republic)",
        "CountryCode": "GE",
        "DialingCode": "+995"
    },
    {
        "Id": 82,
        "Name": "Germany",
        "CountryCode": "DE",
        "DialingCode": "+49"
    },
    {
        "Id": 83,
        "Name": "Ghana (Former Gold Coast)",
        "CountryCode": "GH",
        "DialingCode": "+233"
    },
    {
        "Id": 84,
        "Name": "Gibraltar",
        "CountryCode": "GI",
        "DialingCode": "+350"
    },
    {
        "Id": 86,
        "Name": "Greece",
        "CountryCode": "GR",
        "DialingCode": "+30"
    },
    {
        "Id": 87,
        "Name": "Greenland",
        "CountryCode": "GL",
        "DialingCode": "+299"
    },
    {
        "Id": 88,
        "Name": "Grenada",
        "CountryCode": "GD",
        "DialingCode": "+1-473"
    },
    {
        "Id": 89,
        "Name": "Guadeloupe",
        "CountryCode": "GP",
        "DialingCode": "+590"
    },
    {
        "Id": 90,
        "Name": "Guam",
        "CountryCode": "GU",
        "DialingCode": "+1-671"
    },
    {
        "Id": 91,
        "Name": "Guatemala",
        "CountryCode": "GT",
        "DialingCode": "+502"
    },
    {
        "Id": 92,
        "Name": "Guinea (Former French Guinea)",
        "CountryCode": "GN",
        "DialingCode": "+224"
    },
    {
        "Id": 93,
        "Name": "Guinea-Bissau (Former Portuguese Guinea)",
        "CountryCode": "GW",
        "DialingCode": "+245"
    },
    {
        "Id": 94,
        "Name": "Guyana (Former British Guiana)",
        "CountryCode": "GY",
        "DialingCode": "+592"
    },
    {
        "Id": 95,
        "Name": "Haiti",
        "CountryCode": "HT",
        "DialingCode": "+509"
    },
    {
        "Id": 98,
        "Name": "Honduras",
        "CountryCode": "HN",
        "DialingCode": "+504"
    },
    {
        "Id": 99,
        "Name": "Hong Kong",
        "CountryCode": "HK",
        "DialingCode": "+852"
    },
    {
        "Id": 100,
        "Name": "Hungary",
        "CountryCode": "HU",
        "DialingCode": "+36"
    },
    {
        "Id": 101,
        "Name": "Iceland",
        "CountryCode": "IS",
        "DialingCode": "+354"
    },
    {
        "Id": 102,
        "Name": "India",
        "CountryCode": "IN",
        "DialingCode": "+91"
    },
    {
        "Id": 103,
        "Name": "Indonesia (Former Netherlands East Indies; Dutch East Indies)",
        "CountryCode": "ID",
        "DialingCode": "+62"
    },
    {
        "Id": 104,
        "Name": "Iran, Islamic Republic of",
        "CountryCode": "IR",
        "DialingCode": "+98"
    },
    {
        "Id": 105,
        "Name": "Iraq",
        "CountryCode": "IQ",
        "DialingCode": "+964"
    },
    {
        "Id": 106,
        "Name": "Ireland",
        "CountryCode": "IE",
        "DialingCode": "+353"
    },
    {
        "Id": 107,
        "Name": "Israel",
        "CountryCode": "IL",
        "DialingCode": "+972"
    },
    {
        "Id": 108,
        "Name": "Italy",
        "CountryCode": "IT",
        "DialingCode": "+39"
    },
    {
        "Id": 109,
        "Name": "Jamaica",
        "CountryCode": "JM",
        "DialingCode": "+1-876"
    },
    {
        "Id": 110,
        "Name": "Japan",
        "CountryCode": "JP",
        "DialingCode": "+81"
    },
    {
        "Id": 111,
        "Name": "Jordan (Former Transjordan)",
        "CountryCode": "JO",
        "DialingCode": "+962"
    },
    {
        "Id": 112,
        "Name": "Kazakstan or Kazakhstan (Former Kazakh Soviet Socialist Republic)",
        "CountryCode": "KZ",
        "DialingCode": "+7"
    },
    {
        "Id": 113,
        "Name": "Kenya (Former British East Africa)",
        "CountryCode": "KE",
        "DialingCode": "+254"
    },
    {
        "Id": 114,
        "Name": "Kiribati (Pronounced keer-ree-bahss) (Former Gilbert Islands)",
        "CountryCode": "KI",
        "DialingCode": "+686"
    },
    {
        "Id": 115,
        "Name": "Korea, Democratic People`s Republic of (North Korea)",
        "CountryCode": "KP",
        "DialingCode": "+850"
    },
    {
        "Id": 116,
        "Name": "Korea, Republic of (South Korea)",
        "CountryCode": "KR",
        "DialingCode": "+82"
    },
    {
        "Id": 117,
        "Name": "Kuwait",
        "CountryCode": "KW",
        "DialingCode": "+965"
    },
    {
        "Id": 118,
        "Name": "Kyrgyzstan (Kyrgyz Republic) (Former Kirghiz Soviet Socialist Republic)",
        "CountryCode": "KG",
        "DialingCode": "+996"
    },
    {
        "Id": 119,
        "Name": "Lao People`s Democratic Republic (Laos)",
        "CountryCode": "LA",
        "DialingCode": "+856"
    },
    {
        "Id": 120,
        "Name": "Latvia (Former Latvian Soviet Socialist Republic)",
        "CountryCode": "LV",
        "DialingCode": "+371"
    },
    {
        "Id": 121,
        "Name": "Lebanon",
        "CountryCode": "LB",
        "DialingCode": "+961"
    },
    {
        "Id": 122,
        "Name": "Lesotho (Former Basutoland)",
        "CountryCode": "LS",
        "DialingCode": "+266"
    },
    {
        "Id": 123,
        "Name": "Liberia",
        "CountryCode": "LR",
        "DialingCode": "+231"
    },
    {
        "Id": 124,
        "Name": "Libya (Libyan Arab Jamahiriya)",
        "CountryCode": "LY",
        "DialingCode": "+218"
    },
    {
        "Id": 125,
        "Name": "Liechtenstein",
        "CountryCode": "LI",
        "DialingCode": "+423"
    },
    {
        "Id": 126,
        "Name": "Lithuania (Former Lithuanian Soviet Socialist Republic)",
        "CountryCode": "LT",
        "DialingCode": "+370"
    },
    {
        "Id": 127,
        "Name": "Luxembourg",
        "CountryCode": "LU",
        "DialingCode": "+352"
    },
    {
        "Id": 128,
        "Name": "Macau",
        "CountryCode": "MO",
        "DialingCode": "+853"
    },
    {
        "Id": 129,
        "Name": "Macedonia, The Former Yugoslav Republic of",
        "CountryCode": "MK",
        "DialingCode": "+389"
    },
    {
        "Id": 130,
        "Name": "Madagascar (Former Malagasy Republic)",
        "CountryCode": "MG",
        "DialingCode": "+261"
    },
    {
        "Id": 131,
        "Name": "Malawi (Former British Central African Protectorate, Nyasaland)",
        "CountryCode": "MW",
        "DialingCode": "+265"
    },
    {
        "Id": 132,
        "Name": "Malaysia",
        "CountryCode": "MY",
        "DialingCode": "+60"
    },
    {
        "Id": 133,
        "Name": "Maldives",
        "CountryCode": "MV",
        "DialingCode": "+960"
    },
    {
        "Id": 134,
        "Name": "Mali (Former French Sudan and Sudanese Republic)",
        "CountryCode": "ML",
        "DialingCode": "+223"
    },
    {
        "Id": 135,
        "Name": "Malta",
        "CountryCode": "MT",
        "DialingCode": "+356"
    },
    {
        "Id": 136,
        "Name": "Marshall Islands (Former Marshall Islands District - Trust Territory of the Pacific Islands)",
        "CountryCode": "MH",
        "DialingCode": "+692"
    },
    {
        "Id": 137,
        "Name": "Martinique (French)",
        "CountryCode": "MQ",
        "DialingCode": "+596"
    },
    {
        "Id": 138,
        "Name": "Mauritania",
        "CountryCode": "MR",
        "DialingCode": "+222"
    },
    {
        "Id": 139,
        "Name": "Mauritius",
        "CountryCode": "MU",
        "DialingCode": "+230"
    },
    {
        "Id": 140,
        "Name": "Mayotte (Territorial Collectivity of Mayotte)",
        "CountryCode": "YT",
        "DialingCode": "+269"
    },
    {
        "Id": 141,
        "Name": "Mexico",
        "CountryCode": "MX",
        "DialingCode": "+52"
    },
    {
        "Id": 142,
        "Name": "Micronesia, Federated States of (Former Ponape, Truk, and Yap Districts - Trust Territory of the Pacific Islands)",
        "CountryCode": "FM",
        "DialingCode": "+691"
    },
    {
        "Id": 143,
        "Name": "Moldova, Republic of",
        "CountryCode": "MD",
        "DialingCode": "+373"
    },
    {
        "Id": 144,
        "Name": "Monaco, Principality of",
        "CountryCode": "MC",
        "DialingCode": "+377"
    },
    {
        "Id": 145,
        "Name": "Mongolia (Former Outer Mongolia)",
        "CountryCode": "MN",
        "DialingCode": "+976"
    },
    {
        "Id": 146,
        "Name": "Montserrat",
        "CountryCode": "MS",
        "DialingCode": "+1-664"
    },
    {
        "Id": 147,
        "Name": "Morocco",
        "CountryCode": "MA",
        "DialingCode": "+212"
    },
    {
        "Id": 148,
        "Name": "Mozambique (Former Portuguese East Africa)",
        "CountryCode": "MZ",
        "DialingCode": "+258"
    },
    {
        "Id": 149,
        "Name": "Myanmar, Union of (Former Burma)",
        "CountryCode": "MM",
        "DialingCode": "+95"
    },
    {
        "Id": 150,
        "Name": "Namibia (Former German Southwest Africa, South-West Africa)",
        "CountryCode": "NA",
        "DialingCode": "+264"
    },
    {
        "Id": 151,
        "Name": "Nauru (Former Pleasant Island)",
        "CountryCode": "NR",
        "DialingCode": "+674"
    },
    {
        "Id": 152,
        "Name": "Nepal",
        "CountryCode": "NP",
        "DialingCode": "+977"
    },
    {
        "Id": 153,
        "Name": "Netherlands",
        "CountryCode": "NL",
        "DialingCode": "+31"
    },
    {
        "Id": 154,
        "Name": "Netherlands Antilles (Former Curacao and Dependencies)",
        "CountryCode": "AN",
        "DialingCode": "+599"
    },
    {
        "Id": 155,
        "Name": "New Caledonia",
        "CountryCode": "NC",
        "DialingCode": "+687"
    },
    {
        "Id": 156,
        "Name": "New Zealand (Aotearoa)",
        "CountryCode": "NZ",
        "DialingCode": "+64"
    },
    {
        "Id": 157,
        "Name": "Nicaragua",
        "CountryCode": "NI",
        "DialingCode": "+505"
    },
    {
        "Id": 158,
        "Name": "Niger",
        "CountryCode": "NE",
        "DialingCode": "+227"
    },
    {
        "Id": 159,
        "Name": "Nigeria",
        "CountryCode": "NG",
        "DialingCode": "+234"
    },
    {
        "Id": 160,
        "Name": "Niue (Former Savage Island)",
        "CountryCode": "NU",
        "DialingCode": "+683"
    },
    {
        "Id": 161,
        "Name": "Norfolk Island",
        "CountryCode": "NF",
        "DialingCode": "+672"
    },
    {
        "Id": 162,
        "Name": "Northern Mariana Islands (Former Mariana Islands District - Trust Territory of the Pacific Islands)",
        "CountryCode": "MP",
        "DialingCode": "+1-670"
    },
    {
        "Id": 163,
        "Name": "Norway",
        "CountryCode": "NO",
        "DialingCode": "+47"
    },
    {
        "Id": 164,
        "Name": "Oman, Sultanate of (Former Muscat and Oman)",
        "CountryCode": "OM",
        "DialingCode": "+968"
    },
    {
        "Id": 165,
        "Name": "Pakistan (Former West Pakistan)",
        "CountryCode": "PK",
        "DialingCode": "+92"
    },
    {
        "Id": 166,
        "Name": "Palau (Former Palau District - Trust Terriroty of the Pacific Islands)",
        "CountryCode": "PW",
        "DialingCode": "+680"
    },
    {
        "Id": 167,
        "Name": "Palestinian State (Proposed)",
        "CountryCode": "PS",
        "DialingCode": "+970"
    },
    {
        "Id": 168,
        "Name": "Panama",
        "CountryCode": "PA",
        "DialingCode": "+507"
    },
    {
        "Id": 169,
        "Name": "Papua New Guinea (Former Territory of Papua and New Guinea)",
        "CountryCode": "PG",
        "DialingCode": "+675"
    },
    {
        "Id": 170,
        "Name": "Paraguay",
        "CountryCode": "PY",
        "DialingCode": "+595"
    },
    {
        "Id": 171,
        "Name": "Peru",
        "CountryCode": "PE",
        "DialingCode": "+51"
    },
    {
        "Id": 172,
        "Name": "Philippines",
        "CountryCode": "PH",
        "DialingCode": "+63"
    },
    {
        "Id": 174,
        "Name": "Poland",
        "CountryCode": "PL",
        "DialingCode": "+48"
    },
    {
        "Id": 175,
        "Name": "Portugal",
        "CountryCode": "PT",
        "DialingCode": "+351"
    },
    {
        "Id": 177,
        "Name": "Qatar, State of",
        "CountryCode": "QA",
        "DialingCode": "+974 "
    },
    {
        "Id": 178,
        "Name": "Reunion (French) (Former Bourbon Island)",
        "CountryCode": "RE",
        "DialingCode": "+262"
    },
    {
        "Id": 179,
        "Name": "Romania",
        "CountryCode": "RO",
        "DialingCode": "+40"
    },
    {
        "Id": 181,
        "Name": "Russian Federation",
        "CountryCode": "RU",
        "DialingCode": "+7"
    },
    {
        "Id": 182,
        "Name": "Rwanda (Rwandese Republic) (Former Ruanda)",
        "CountryCode": "RW",
        "DialingCode": "+250"
    },
    {
        "Id": 183,
        "Name": "Saint Helena",
        "CountryCode": "SH",
        "DialingCode": "+290"
    },
    {
        "Id": 184,
        "Name": "Saint Kitts and Nevis (Former Federation of Saint Christopher and Nevis)",
        "CountryCode": "KN",
        "DialingCode": "+1-869"
    },
    {
        "Id": 185,
        "Name": "Saint Lucia",
        "CountryCode": "LC",
        "DialingCode": "+1-758"
    },
    {
        "Id": 186,
        "Name": "Saint Pierre and Miquelon",
        "CountryCode": "PM",
        "DialingCode": "+508"
    },
    {
        "Id": 187,
        "Name": "Saint Vincent and the Grenadines",
        "CountryCode": "VC",
        "DialingCode": "+1-784"
    },
    {
        "Id": 188,
        "Name": "Samoa (Former Western Samoa)",
        "CountryCode": "WS",
        "DialingCode": "+685"
    },
    {
        "Id": 189,
        "Name": "San Marino",
        "CountryCode": "SM",
        "DialingCode": "+378"
    },
    {
        "Id": 190,
        "Name": "Sao Tome and Principe",
        "CountryCode": "ST",
        "DialingCode": "+239"
    },
    {
        "Id": 191,
        "Name": "Saudi Arabia",
        "CountryCode": "SA",
        "DialingCode": "+966"
    },
    {
        "Id": 193,
        "Name": "Senegal",
        "CountryCode": "SN",
        "DialingCode": "+221"
    },
    {
        "Id": 194,
        "Name": "Seychelles",
        "CountryCode": "SC",
        "DialingCode": "+248"
    },
    {
        "Id": 195,
        "Name": "Sierra Leone",
        "CountryCode": "SL",
        "DialingCode": "+232"
    },
    {
        "Id": 196,
        "Name": "Singapore",
        "CountryCode": "SG",
        "DialingCode": "+65"
    },
    {
        "Id": 197,
        "Name": "Slovakia",
        "CountryCode": "SK",
        "DialingCode": "+421"
    },
    {
        "Id": 198,
        "Name": "Slovenia",
        "CountryCode": "SI",
        "DialingCode": "+386"
    },
    {
        "Id": 199,
        "Name": "Solomon Islands (Former British Solomon Islands)",
        "CountryCode": "SB",
        "DialingCode": "+677"
    },
    {
        "Id": 200,
        "Name": "Somalia (Former Somali Republic, Somali Democratic Republic)",
        "CountryCode": "SO",
        "DialingCode": "+252"
    },
    {
        "Id": 201,
        "Name": "South Africa (Former Union of South Africa)",
        "CountryCode": "ZA",
        "DialingCode": "+27"
    },
    {
        "Id": 203,
        "Name": "Spain",
        "CountryCode": "ES",
        "DialingCode": "+34"
    },
    {
        "Id": 204,
        "Name": "Sri Lanka (Former Serendib, Ceylon)",
        "CountryCode": "LK",
        "DialingCode": "+94"
    },
    {
        "Id": 205,
        "Name": "Sudan (Former Anglo-Egyptian Sudan)",
        "CountryCode": "SD",
        "DialingCode": "+249"
    },
    {
        "Id": 206,
        "Name": "Suriname (Former Netherlands Guiana, Dutch Guiana)",
        "CountryCode": "SR",
        "DialingCode": "+597"
    },
    {
        "Id": 208,
        "Name": "Swaziland, Kingdom of",
        "CountryCode": "SZ",
        "DialingCode": "+268"
    },
    {
        "Id": 209,
        "Name": "Sweden",
        "CountryCode": "SE",
        "DialingCode": "+46"
    },
    {
        "Id": 210,
        "Name": "Switzerland",
        "CountryCode": "CH",
        "DialingCode": "+41"
    },
    {
        "Id": 211,
        "Name": "Syria (Syrian Arab Republic) (Former United Arab Republic - with Egypt)",
        "CountryCode": "SY",
        "DialingCode": "+963"
    },
    {
        "Id": 212,
        "Name": "Taiwan (Former Formosa)",
        "CountryCode": "TW",
        "DialingCode": "+886"
    },
    {
        "Id": 213,
        "Name": "Tajikistan (Former Tajik Soviet Socialist Republic)",
        "CountryCode": "TJ",
        "DialingCode": "+992"
    },
    {
        "Id": 214,
        "Name": "Tanzania, United Republic of (Former United Republic of Tanganyika and Zanzibar)",
        "CountryCode": "TZ",
        "DialingCode": "+255"
    },
    {
        "Id": 215,
        "Name": "Thailand (Former Siam)",
        "CountryCode": "TH",
        "DialingCode": "+66"
    },
    {
        "Id": 217,
        "Name": "Tokelau",
        "CountryCode": "TK",
        "DialingCode": "+690"
    },
    {
        "Id": 218,
        "Name": "Tonga, Kingdom of (Former Friendly Islands)",
        "CountryCode": "TO",
        "DialingCode": "+676"
    },
    {
        "Id": 219,
        "Name": "Trinidad and Tobago",
        "CountryCode": "TT",
        "DialingCode": "+1-868"
    },
    {
        "Id": 221,
        "Name": "Tunisia",
        "CountryCode": "TN",
        "DialingCode": "+216"
    },
    {
        "Id": 222,
        "Name": "Turkey",
        "CountryCode": "TR",
        "DialingCode": "+90"
    },
    {
        "Id": 223,
        "Name": "Turkmenistan (Former Turkmen Soviet Socialist Republic)",
        "CountryCode": "TM",
        "DialingCode": "+993"
    },
    {
        "Id": 224,
        "Name": "Turks and Caicos Islands",
        "CountryCode": "TC",
        "DialingCode": "+1-649"
    },
    {
        "Id": 225,
        "Name": "Tuvalu (Former Ellice Islands)",
        "CountryCode": "TV",
        "DialingCode": "+688"
    },
    {
        "Id": 226,
        "Name": "Uganda, Republic of",
        "CountryCode": "UG",
        "DialingCode": "+256"
    },
    {
        "Id": 227,
        "Name": "Ukraine (Former Ukrainian National Republic, Ukrainian State, Ukrainian Soviet Socialist Republic)",
        "CountryCode": "UA",
        "DialingCode": "+380"
    },
    {
        "Id": 228,
        "Name": "United Arab Emirates (UAE) (Former Trucial Oman, Trucial States)",
        "CountryCode": "AE",
        "DialingCode": "+971"
    },
    {
        "Id": 229,
        "Name": "United Kingdom (Great Britain / UK)",
        "CountryCode": "GB",
        "DialingCode": "+44"
    },
    {
        "Id": 230,
        "Name": "United States",
        "CountryCode": "US",
        "DialingCode": "+1"
    },
    {
        "Id": 232,
        "Name": "Uruguay, Oriental Republic of (Former Banda Oriental, Cisplatine Province)",
        "CountryCode": "UY",
        "DialingCode": "+598"
    },
    {
        "Id": 233,
        "Name": "Uzbekistan (Former UZbek Soviet Socialist Republic)",
        "CountryCode": "UZ",
        "DialingCode": "+998"
    },
    {
        "Id": 234,
        "Name": "Vanuatu (Former New Hebrides)",
        "CountryCode": "VU",
        "DialingCode": "+678"
    },
    {
        "Id": 235,
        "Name": "Vatican City State (Holy See)",
        "CountryCode": "VA",
        "DialingCode": "+418"
    },
    {
        "Id": 236,
        "Name": "Venezuela",
        "CountryCode": "VE",
        "DialingCode": "+58"
    },
    {
        "Id": 237,
        "Name": "Vietnam",
        "CountryCode": "VN",
        "DialingCode": "+84"
    },
    {
        "Id": 238,
        "Name": "Virgin Islands, British",
        "CountryCode": "VI",
        "DialingCode": "+1-284"
    },
    {
        "Id": 239,
        "Name": "Virgin Islands, United States (Former Danish West Indies)",
        "CountryCode": "VQ",
        "DialingCode": "+1-340"
    },
    {
        "Id": 240,
        "Name": "Wallis and Futuna Islands",
        "CountryCode": "WF",
        "DialingCode": "+681"
    },
    {
        "Id": 242,
        "Name": "Yemen",
        "CountryCode": "YE",
        "DialingCode": "+967"
    },
    {
        "Id": 245,
        "Name": "Zambia, Republic of (Former Northern Rhodesia)",
        "CountryCode": "ZM",
        "DialingCode": "+260"
    },
    {
        "Id": 246,
        "Name": "Zimbabwe, Republic of (Former Southern Rhodesia, Rhodesia)",
        "CountryCode": "ZW",
        "DialingCode": "+263"
    },
    {
        "Id": 247,
        "Name": "Puerto Rico",
        "CountryCode": "PR",
        "DialingCode": "+1-787"
    },
    {
        "Id": 248,
        "Name": "Puerto Rico",
        "CountryCode": "PR",
        "DialingCode": "+1-939"
    },
    {
        "Id": 249,
        "Name": "Dominican Republic",
        "CountryCode": "DO",
        "DialingCode": "+1-809"
    },
    {
        "Id": 250,
        "Name": "Dominican Republic",
        "CountryCode": "DO",
        "DialingCode": "+1-829"
    },
    {
        "Id": 251,
        "Name": "Serbia",
        "CountryCode": "RS",
        "DialingCode": "+381"
    }
];
